import { ReactNode } from "react";
import { Box, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PasswordIcon from "@mui/icons-material/Password";
import VerifiedIcon from "@mui/icons-material/Verified";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import GppBadOutlined from "@mui/icons-material/GppBadOutlined";
import GppMaybeOutlined from "@mui/icons-material/GppMaybeOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import DesktopWindowsOutlined from "@mui/icons-material/DesktopWindowsOutlined";
import PhoneIphoneOutlined from "@mui/icons-material/PhoneIphoneOutlined";
import TabletMacOutlined from "@mui/icons-material/TabletMacOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import {
  AttachFileOutlined,
  CancelOutlined,
  FactCheckOutlined,
  HelpOutline,
  HowToRegOutlined,
  LinkOutlined,
  MarkEmailReadOutlined,
  PhonelinkRingOutlined,
  TouchAppOutlined,
  UndoOutlined,
} from "@mui/icons-material";
import Approved from "assets/icons/AuditEvents/Approved.svg";
import Completed from "assets/icons/AuditEvents/Completed.svg";
import Declined from "assets/icons/AuditEvents/Declined.svg";
import Delegated from "assets/icons/AuditEvents/Delegated.svg";
import Opened from "assets/icons/AuditEvents/Opened.svg";
import Rejected from "assets/icons/AuditEvents/Rejected.svg";
import Requested from "assets/icons/AuditEvents/Requested.svg";
import Resent from "assets/icons/AuditEvents/Resent.svg";
import Scheduled from "assets/icons/AuditEvents/Scheduled.svg";
import Sent from "assets/icons/AuditEvents/Sent.svg";
import SentForApproval from "assets/icons/AuditEvents/SentForApproval.svg";
import Signed from "assets/icons/AuditEvents/Signed.svg";
import Updated from "assets/icons/AuditEvents/Updated.svg";
import Voided from "assets/icons/AuditEvents/Voided.svg";
import strings from "global/constants/StringConstants";
import { info } from "theme/colors";
import { handleErrorMessage } from "helpers/methods";
import {
  AuditChainVerification,
  RequestAudit,
  RequestParticipant,
} from "models/interfaces";
import { StatusChipTone } from "global/components/CustomStatusChip/CustomStatusChip";
import { formatAuditDate } from "./ViewRequestStatusMeta";
import { resendSignRequest } from "../MySignRequestService";
import { hasAccessTo } from "layouts/Config";

/**
 * Glyph and disc tint for one audit event. The event icons are pre-coloured
 * SVG assets, so the disc background travels with the icon rather than coming
 * from the screen tokens.
 *
 * `description` is only consulted for the fallback classification below —
 * `eventType` isn't guaranteed to spell out newer event kinds (e.g. the
 * in-person host-link / post-signing-email events) the same way its
 * human-readable description does.
 */
export const getAuditStatusMeta = (status: string, description?: string) => {
  switch (status?.toLowerCase()) {
    case strings.auditStatuses.requested:
      return { icon: <img src={Requested} alt="" />, bgColor: "#E3F2FD" };
    case strings.auditStatuses.sent:
      return { icon: <img src={Sent} alt="" />, bgColor: "#E8EAF6" };
    case strings.auditStatuses.opened:
      return { icon: <img src={Opened} alt="" />, bgColor: "#E3F2FD" };
    case strings.auditStatuses.signed:
      return { icon: <img src={Signed} alt="" />, bgColor: "#FFF8E1" };
    case strings.auditStatuses.completed:
      return { icon: <img src={Completed} alt="" />, bgColor: "#D1FFFC" };
    case strings.auditStatuses.voided:
      return { icon: <img src={Voided} alt="" />, bgColor: "#FDECEA" };
    case strings.auditStatuses.rejected:
      return { icon: <img src={Rejected} alt="" />, bgColor: "#FDECEA" };
    case strings.auditStatuses.edited:
      return { icon: <img src={Updated} alt="" />, bgColor: "#C9EEFF" };
    case strings.auditStatuses.scheduled:
      return { icon: <img src={Scheduled} alt="" />, bgColor: "#EDE7F6" };
    case strings.auditStatuses.declined:
      return { icon: <img src={Declined} alt="" />, bgColor: "#FDECEA" };
    case strings.auditStatuses.resent:
      return { icon: <img src={Resent} alt="" />, bgColor: "#E8F5E9" };
    case strings.auditStatuses.sentForApproval:
      return { icon: <img src={SentForApproval} alt="" />, bgColor: "#FFF8E1" };
    case strings.auditStatuses.approved:
      return { icon: <img src={Approved} alt="" />, bgColor: "#E6F4EA" };
    case strings.auditStatuses.delegated:
      return { icon: <img src={Delegated} alt="" />, bgColor: "#E0F2F1" };
    case strings.auditStatuses.sentOtp:
      return {
        icon: <PasswordIcon sx={{ fontSize: 16, color: "#3949ab" }} />,
        bgColor: "#E8EAF6",
      };
    case strings.auditStatuses.codeVerified:
      return {
        icon: <VerifiedIcon sx={{ fontSize: 16, color: "#00796b" }} />,
        bgColor: "#E0F2F1",
      };
    case strings.auditStatuses.attachmentUploaded:
      return {
        icon: <AttachFileOutlined sx={{ fontSize: 16, color: "#3949ab" }} />,
        bgColor: "#E8EAF6",
      };
    case strings.auditStatuses.attachmentRemoved:
      return {
        icon: <AttachFileOutlined sx={{ fontSize: 16, color: "#b71c1c" }} />,
        bgColor: "#FDECEA",
      };
    case strings.auditStatuses.fieldDataSubmitted:
      return { icon: <img src={Updated} alt="" />, bgColor: info.lightest ?? info.light };
    case strings.auditStatuses.inPersonStarted:
      return {
        icon: <TouchAppOutlined sx={{ fontSize: 16, color: "#3949ab" }} />,
        bgColor: "#E8EAF6",
      };
    case strings.auditStatuses.inPersonHandoff:
      return {
        icon: <PhonelinkRingOutlined sx={{ fontSize: 16, color: "#5e35b1" }} />,
        bgColor: "#EDE7F6",
      };
    case strings.auditStatuses.signedInPerson:
      return {
        icon: <HowToRegOutlined sx={{ fontSize: 16, color: "#8f5b06" }} />,
        bgColor: "#FFF8E1",
      };
    case strings.auditStatuses.inPersonReturned:
      return {
        icon: <UndoOutlined sx={{ fontSize: 16, color: "#5e35b1" }} />,
        bgColor: "#EDE7F6",
      };
    case strings.auditStatuses.inPersonAbandoned:
      return {
        icon: <CancelOutlined sx={{ fontSize: 16, color: "#c62828" }} />,
        bgColor: "#FDECEA",
      };
    case strings.auditStatuses.inPersonHostLinkSent:
      return {
        icon: <LinkOutlined sx={{ fontSize: 16, color: "#3949ab" }} />,
        bgColor: "#E8EAF6",
      };
    case strings.auditStatuses.inPersonConsentAccepted:
      return {
        icon: <FactCheckOutlined sx={{ fontSize: 16, color: "#00796b" }} />,
        bgColor: "#E0F2F1",
      };
    default: {
      // `eventType` isn't guaranteed to carry these newer event kinds the way
      // its description does, so classify off both, combined — catches every
      // consent flavour (requester/host/signer) and phrasing variant without
      // enumerating each exact string.
      const haystack = `${status ?? ""} ${description ?? ""}`.toLowerCase();
      if (haystack.includes("consent")) {
        return {
          icon: <FactCheckOutlined sx={{ fontSize: 16, color: "#00796b" }} />,
          bgColor: "#E0F2F1",
        };
      }
      if (haystack.includes("host link")) {
        return {
          icon: <LinkOutlined sx={{ fontSize: 16, color: "#3949ab" }} />,
          bgColor: "#E8EAF6",
        };
      }
      if (haystack.includes("email added") || haystack.includes("email set")) {
        return {
          icon: <MarkEmailReadOutlined sx={{ fontSize: 16, color: "#00796b" }} />,
          bgColor: "#E0F2F1",
        };
      }
      return {
        icon: <HelpOutline sx={{ fontSize: 16, color: "#9E9E9E" }} />,
        bgColor: "#EEEEEE",
      };
    }
  }
};

/** Overflow menu opened from a signer/approver row. */
export const requestParticipantsMenu = (
  anchorEl: any,
  isMenuOpen: boolean,
  handleMenuClose: (event: React.MouseEvent<HTMLElement>) => void,
  handleIndividualAudit: (email: string) => void,
  selectedParticipant: RequestParticipant | undefined,
  setSelectedParticipant: (participant: RequestParticipant | undefined) => void,
  setShowFullAuditBtn: (value: boolean) => void,
  reqId: string,
  setLoader: (value: boolean) => void,
  status: string,
  isManualReminderAvailable: boolean = true,
  manualReminderTooltip: string = "",
  fetchData?: () => void,
) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={(e: React.MouseEvent<HTMLElement>) => {
        handleMenuClose(e);
        setSelectedParticipant(undefined);
        setShowFullAuditBtn(false);
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      MenuListProps={{ "aria-label": "Recipient actions" }}
      sx={{
        top: "10px",
      }}
    >
      {selectedParticipant?.status === strings.pending &&
        status === strings.pending &&
        hasAccessTo(strings.resources.sign, strings.sendPermission) && (
          <Tooltip
            title={isManualReminderAvailable ? "" : manualReminderTooltip}
            placement="left"
            arrow
            disableHoverListener={isManualReminderAvailable}
            describeChild
          >
            <MenuItem
              disabled={!isManualReminderAvailable}
              sx={
                isManualReminderAvailable
                  ? undefined
                  : {
                      "&.Mui-disabled": {
                        pointerEvents: "auto",
                        cursor: "default",
                        "&:hover": { bgcolor: "transparent" },
                      },
                    }
              }
              onClick={async (e: React.MouseEvent<HTMLElement>) => {
                if (!isManualReminderAvailable) return;
                try {
                  setLoader(true);
                  handleMenuClose(e);
                  await resendSignRequest(reqId, selectedParticipant.email);
                  fetchData?.();
                  setLoader(false);
                } catch (error: any) {
                  setLoader(false);
                  handleErrorMessage(error);
                }
              }}
            >
              <Typography variant="subtitle2">{strings.Resend}</Typography>
              {!isManualReminderAvailable && (
                <LockOutlinedIcon
                  sx={{ fontSize: 14, color: "text.disabled", ml: "auto" }}
                />
              )}
            </MenuItem>
          </Tooltip>
        )}
      <MenuItem
        onClick={(e) => {
          handleIndividualAudit(selectedParticipant?.email!);
          setShowFullAuditBtn(true);
          handleMenuClose(e);
        }}
      >
        <Typography variant="subtitle2">See audit</Typography>
      </MenuItem>
    </Menu>
  );
};

/** What the Audit Trail card's integrity bar shows, before or after a check. */
export interface AuditIntegrityMeta {
  /** Status-chip tone of a result; undefined before the first check. */
  tone?: StatusChipTone;
  icon: ReactNode;
  title: string;
  detail: string;
  /** Extra detail on hover, e.g. the final fingerprint. */
  tooltip?: string;
  action: string;
}

/**
 * Integrity bar copy + icon. BROKEN names the first failing event so support
 * can look at exactly that row; VALID's tooltip carries the final fingerprint
 * the certificate prints, so the two can be compared.
 */
export const getAuditIntegrityMeta = (
  result?: AuditChainVerification,
): AuditIntegrityMeta => {
  if (!result) {
    return {
      icon: <ShieldOutlined className="integrity-icon" />,
      title: "Tamper-evident trail",
      detail: "Check that no event has been altered",
      action: "Verify",
    };
  }
  switch (result.status) {
    case "VALID":
      return {
        tone: "success",
        icon: <VerifiedUserOutlined className="integrity-icon" />,
        title: "Integrity verified",
        detail:
          `${result.eventCount} ${result.eventCount === 1 ? "event" : "events"} unchanged` +
          (result.sealedFrom ? ` · sealed ${formatAuditDate(result.sealedFrom)}` : ""),
        tooltip: result.finalHash ? `Final fingerprint: ${result.finalHash}` : undefined,
        action: "Re-check",
      };
    case "BROKEN":
      return {
        tone: "danger",
        icon: <GppBadOutlined className="integrity-icon" />,
        title: "Trail altered",
        detail:
          result.reason === "MIXED_SEALED_UNSEALED"
            ? "An event was added outside the sealed trail"
            : result.reason === "UNKNOWN_KEY" || result.reason === "UNSUPPORTED_VERSION"
              ? `Event ${result.firstBrokenSeq} can't be checked by this service`
              : `Event ${result.firstBrokenSeq} no longer matches its seal`,
        action: "Re-check",
      };
    default:
      return {
        tone: "neutral",
        icon: <GppMaybeOutlined className="integrity-icon" />,
        title: "Not sealed",
        detail: "Recorded before tamper protection was enabled",
        action: "Re-check",
      };
  }
};

/** Glyph for the device an audit event came from; a cog for system events. */
export const getAuditDeviceIcon = (deviceType?: RequestAudit["deviceType"], description?: string) => {
  if (description === "System") return <SettingsOutlined />;
  switch (deviceType) {
    case "MOBILE":
      return <PhoneIphoneOutlined />;
    case "TABLET":
      return <TabletMacOutlined />;
    default:
      return <DesktopWindowsOutlined />;
  }
};
